<template>
  <UCard
    :class="[product.most_popular ? 'ring-2 ring-yellow-400' : '']"
    :ui="{
      root: 'relative bg-neutral-900/60 backdrop-blur-lg overflow-hidden flex flex-col min-h-full',
      header: 'relative z-[3]',
      body: 'border-none h-full',
      footer: 'relative z-[3] pt-0 pt-2 mt-auto',
    }"
  >
    <template #header>
      <div class="space-y-2">
        <div class="truncate font-extrabold text-yellow-400 uppercase">
          {{ product.name }}
        </div>
        <div
          v-if="product.price_starting_from && !product.hide_price"
          class="-mb-0 text-base font-semibold text-neutral-400"
        >
          Starting From
        </div>
        <div
          v-if="!product.hide_price"
          class="flex items-end space-x-2 md:block lg:flex"
        >
          <div v-if="price != 0" class="text-5xl font-extrabold">
            £{{ formatPrice(price) }}
          </div>
          <div v-if="price == 0" class="text-5xl font-extrabold">FREE</div>
          <div v-if="price != 0" class="text-lg font-semibold text-yellow-400">
            /per {{ product.price_per_type }}
          </div>
        </div>
        <div
          v-if="product.excludes_vat && !product.hide_price"
          class="-mt-2.5 font-mono text-xs font-bold text-neutral-600 uppercase"
        >
          Price (Excl. VAT)
        </div>
        <div
          v-if="product.description"
          class="line-clamp-3 max-w-prose text-base font-medium text-neutral-400"
        >
          {{ product.description }}
        </div>
      </div>
    </template>
    <div
      v-if="product.most_popular"
      class="absolute inset-0 h-full w-full animate-pulse opacity-20"
      style="animation-duration: 5s"
    >
      <BrandTopography />
    </div>
    <div
      v-if="product.most_popular"
      class="absolute inset-0 z-[1] h-full w-full bg-gradient-to-b from-transparent via-neutral-950/50 to-neutral-950"
    />
    <UTooltip
      v-if="product.most_popular"
      text="Most Popular"
      :content="{
        align: 'center',
        side: 'left',
        sideOffset: 8,
      }"
      :ui="{
        content: 'bg-neutral-950/80 backdrop-blur-lg',
        text: 'font-bold text-yellow-400',
      }"
      :delay-duration="0"
    >
      <UBadge
        class="absolute top-3 right-3 z-[3] rounded-full bg-neutral-800 p-1 backdrop-blur-lg hover:bg-neutral-800/80"
      >
        <UIcon name="i-lucide-flame" class="size-6 text-yellow-400" />
      </UBadge>
    </UTooltip>
    <div class="relative z-[2] space-y-6">
      <ul
        :class="[
          'grid gap-4',
          productsCount === 1
            ? 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
            : '',
          productsCount === 2 ? 'xl:grid-cols-2' : '',
          productsCount === 1 ? 'grid-cols-1' : '',
        ]"
      >
        <li v-if="!showLocation" class="flex items-start space-x-2">
          <UIcon
            name="i-lucide-map-pin"
            class="size-5 shrink-0 text-yellow-400"
          />
          <span class="text-sm font-bold"> Location: {{ location }}</span>
        </li>
        <li v-if="product.availability" class="flex items-start space-x-2">
          <UIcon
            name="i-lucide-calendar-days"
            class="size-5 shrink-0 text-yellow-400"
          />
          <span class="text-sm font-bold capitalize">
            Available: {{ product.availability }}
          </span>
        </li>
        <li v-if="product.duration" class="flex items-start space-x-2">
          <UIcon
            name="i-lucide-clock"
            class="size-5 shrink-0 text-yellow-400"
          />
          <span class="text-sm font-bold">
            Duration: {{ product.duration }}
          </span>
        </li>
        <li v-if="product.age_range" class="flex items-start space-x-2">
          <UIcon name="i-lucide-cake" class="size-5 shrink-0 text-yellow-400" />
          <span class="text-sm font-bold"> Ages: {{ product.age_range }} </span>
        </li>
        <li v-if="product.minimum_qty" class="flex items-start space-x-2">
          <UIcon
            name="i-lucide-users"
            class="size-5 shrink-0 text-yellow-400"
          />
          <span class="text-sm font-bold">
            <span v-if="product.minimum_qty">
              {{ product.minimum_qty }}
            </span>
          </span>
        </li>
        <li v-if="product.large_group" class="flex items-start space-x-2">
          <UIcon
            name="i-lucide-user-plus"
            class="size-5 shrink-0 text-yellow-400"
          />
          <span class="text-sm font-bold">
            Larger Group? (<NuxtLink
              to="/contact"
              class="text-yellow-400 hover:underline"
              >Get In Touch</NuxtLink
            >)
          </span>
        </li>
        <li
          v-if="product.equipment_included !== 'No'"
          class="flex items-start space-x-2"
        >
          <UIcon
            name="i-lucide-grid-2x2-plus"
            class="size-5 shrink-0 text-yellow-400"
          />
          <span class="text-sm font-bold">
            {{ product.equipment_included }}
          </span>
        </li>
        <li
          v-for="(feature, fIndex) in product.features"
          v-if="product.features"
          :key="fIndex"
          class="flex items-start space-x-2"
        >
          <UIcon
            name="i-lucide-circle-check"
            class="size-5 shrink-0 text-yellow-400"
          />
          <span class="text-sm font-bold">
            {{ feature.feature }}
          </span>
        </li>
        <li
          v-for="(addon, aIndex) in addons"
          v-if="addons.length > 0 && productsCount === 1"
          :key="aIndex"
          class="flex items-start space-x-2"
        >
          <UIcon
            name="i-lucide-circle-plus"
            class="size-5 shrink-0 text-yellow-400"
          />
          <span class="text-sm font-bold">
            {{ addon.name }}: £{{ formatPrice(addon.content.price) }}
          </span>
        </li>
      </ul>
      <USeparator
        v-if="addons.length > 0 && productsCount !== 1"
        label="Addons"
        size="md"
        :ui="{
          label: 'font-bold uppercase text-yellow-400',
        }"
      />
      <ul v-if="addons.length > 0 && productsCount !== 1" class="space-y-2">
        <li
          v-for="(addon, aIndex) in addons"
          :key="aIndex"
          class="flex items-start space-x-2"
        >
          <UIcon
            name="i-lucide-circle-plus"
            class="size-5 shrink-0 text-yellow-400"
          />
          <span v-if="addon.content.price !== ''" class="text-sm font-bold">
            {{ addon.name }}:
            <span v-if="addon.content.starting_from">From</span> £{{
              addon.content.price
            }}
          </span>
          <span v-else class="text-sm font-bold">
            {{ addon.name }}
          </span>
        </li>
      </ul>
      <USeparator
        v-if="product.enable_pricing_table"
        label="Group Pricing"
        size="md"
        :ui="{
          label: 'font-bold uppercase text-yellow-400',
        }"
      />
      <div
        v-if="product.enable_pricing_table"
        class="overflow-hidden rounded-lg shadow-lg"
      >
        <table
          v-if="productsCount !== 1"
          class="w-full border-separate bg-neutral-800 text-sm"
          style="border-spacing: 1px"
        >
          <thead class="rounded-t-lg">
            <tr class="bg-neutral-950/80 font-bold text-yellow-400">
              <th
                v-for="(cell, cIndex) in pricingTable"
                :key="cIndex"
                class="p-2"
              >
                {{ cell.content }}
              </th>
            </tr>
          </thead>
          <tbody class="rounded-b-lg">
            <tr
              v-for="(row, rIndex) in product.pricing_table"
              :key="rIndex"
              class="bg-neutral-900 font-semibold"
            >
              <td
                v-for="(cell, cIndex) in row.table_cells"
                :key="cIndex"
                class="p-1 text-center"
              >
                {{ cell.content }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <template #footer>
      <UButton
        v-if="
          product.booking_type === 'link' && bookingLink.linktype !== 'story'
        "
        :to="bookingLink.url"
        target="_blank"
        size="xl"
        class="w-full cursor-pointer justify-center text-center"
        @click="
          // @ts-expect-error - gtag types are not fully typed
          proxy.gtag('consent', 'book_now_clicked', {
            event_category: 'booking',
            event_label: 'book_now_button',
          });
          $clientPosthog?.capture('book_now_clicked');
        "
      >
        {{ bookingLink.title || 'Book Now' }}
      </UButton>
      <UButton
        v-if="
          product.booking_type === 'link' && bookingLink.linktype === 'story'
        "
        :to="`/${bookingLink.cached_url}`"
        size="xl"
        class="w-full cursor-pointer justify-center text-center"
        @click="
          // @ts-expect-error - gtag types are not fully typed
          proxy.gtag('consent', 'book_now_clicked', {
            event_category: 'booking',
            event_label: 'book_now_button',
          });
          $clientPosthog?.capture('book_now_clicked');
        "
      >
        {{ bookingLink.title || 'Book Now' }}
      </UButton>
      <UModal
        v-if="product.booking_type === 'form'"
        v-model:open="modalState"
        :title="bookingForm.button || 'Enquire Now'"
        description="Get in touch to make your booking."
        :close="{
          color: 'primary',
          variant: 'outline',
          class: 'rounded-full',
        }"
        :ui="{
          overlay: 'bg-neutral-950/60	backdrop-blur',
          content: 'bg-neutral-950/60 backdrop-blur-xl overflow-y-auto',
        }"
      >
        <UButton
          size="xl"
          class="w-full cursor-pointer justify-center text-center"
          @click="
            // @ts-expect-error - gtag types are not fully typed
            proxy.gtag('consent', 'enquire_now_clicked', {
              event_category: 'booking',
              event_label: 'enquire_now_button',
            });
            $clientPosthog?.capture('enquire_now_clicked');
          "
        >
          {{ bookingForm.button || 'Enquire Now' }}
        </UButton>

        <template #body>
          <LazyMarketingFormBooking
            :blok="props.blok"
            :form="bookingForm"
            :product="product"
            @submitted="closeModal"
          />
        </template>
      </UModal>
    </template>
  </UCard>
</template>

<script setup lang="ts">
import type {
  ActivityStoryblok,
  AddonStoryblok,
} from '~/types/component-types-sb.d.ts';

interface Props {
  blok: {
    activities?: (ActivityStoryblok | string)[];
  };
  product: ActivityStoryblok;
  productsCount?: number;
  location?: string;
  showLocation?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  productsCount: 0,
  location: '',
  showLocation: false,
});

const { proxy } = useScriptGoogleAnalytics();
const { fetchAddons } = useStoryblokClient();
const { formatPrice } = useFormatPrice();

const modalState = ref(false);
const addons = ref<AddonStoryblok[]>([]);

const bookingForm = computed(
  () => props.product.booking_form?.[0] || { button: 'Enquire Now' }
);
const bookingLink = computed(() => {
  const link = props.product.booking_link?.[0]?.link;
  return {
    linktype: link?.linktype || 'url',
    url: link?.url || '',
    title: link?.title || 'Book Now',
    cached_url: link?.cached_url || '',
  };
});
const pricingTable = computed(
  () => props.product.pricing_table_heading?.[0]?.table_cells || []
);
const price = computed(() => Number(props.product.price || 0));

const closeModal = () => {
  modalState.value = false;
};

// Load Addons and set them in the reactive `addons` array
const loadAddons = async () => {
  try {
    const [stories] = await Promise.all([fetchAddons()]);
    addons.value = stories.filter((story) =>
      props.product.addons?.some((productAddon) => productAddon === story.uuid)
    );
  } catch (error) {
    console.error(error);
  }
};

if (
  props.product &&
  props.product.addons &&
  props.product.addons.length !== 0
) {
  await loadAddons();
}

// Watch for changes in the product to reset addons
watch(
  () => props.product,
  () => {
    addons.value = []; // Reset addons when the product changes
    loadAddons(); // Reload addons for the new product
  },
  { immediate: true } // Ensure this runs when the component is mounted and on changes
);
</script>
